import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { useLazyQuery, useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { Alert, Chip, Grid2 as Grid } from "@mui/material";
import { Button, Checkbox, FormGroup, Select, showSnackbar } from "@cofano/cck";
import { SelectProps } from "@cofano/cck/Select/Select";

import DatePicker from "components/cck/DatePicker";
import { request } from "components/utils/Request2";
import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import NumberInput from "components/cck/NumberInput";

import { Language } from "imports/Utils";

import { getOpenHoursWindow } from "view/locations/utils/LocationUtils";
import LocationSelect, { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import CraneSpeedDialog from "view/availability/components/dialogs/CraneSpeedDialog";
import { AvailabilityRepeatType, availabilitySchema, AvailabilitySchemaInput, AvailabilitySchemaOutput } from "view/availability/utils/availability.schema";

import { FormattedDate } from "utils/DateUtils";

import { LocationType, CraneType, LocationAvailabilityType } from "__generated__/enums";
import { GetLocationAvailabilityDocument } from "__generated__/view/availability/graphql/quayAvailability.queries.generated";
import { SaveLocationAvailabilityDocument, SaveRepeatedLocationAvailabilityDocument } from "__generated__/view/availability/graphql/quayAvailability.mutations.generated";
import { GetLocationForAvailabilityDocument, GetLocationForAvailabilityQuery } from "__generated__/view/locations/graphql/location.queries.generated";

type AvailabilityDialogProps = {
	open: boolean;
	onClose: () => void;
	defaultLocation?: { id: number; description: string; }
	availabilityId?: number;
	refetch: () => void;
	presetAvailabilityType?: Exclude<LocationAvailabilityType, LocationAvailabilityType.TerminalTransferAvailability> | null;
	locationTypes?: LocationType[] | null;
};

const AvailabilityCreateEditDialog = ({
	open,
	onClose,
	defaultLocation,
	availabilityId,
	refetch,
	presetAvailabilityType,
	locationTypes,
}: AvailabilityDialogProps) => {
	const [ craneSpeedModalOpen, openCraneSpeedModal ] = useState(false);
	const [ location, setLocation ] = useState<NonNullable<GetLocationForAvailabilityQuery["location"]> | null>(null);

	const defaultValues = {
		id: null,
		location: defaultLocation ?? null,
		type: presetAvailabilityType ?? LocationAvailabilityType.CraneAvailability,
		crane: null,
		amountOfCranes: null,
		craneSpeed: null,
		fromDateTime: null,
		toDateTime: null,
		bolderFrom: null,
		bolderTo: null,
		maxWidthInMeters: null,
		overflowLocations: null,
		canHandleOOG: false,
		canHandleBundles: false,
		fullContainersAllowed: false,
		twinningAllowed: false,
		repeatType: AvailabilityRepeatType.None,
		repeatDayInterval: null,
		endDate: null,
		days: null,
	};

	const { control, handleSubmit, reset, clearErrors, watch, setValue } = useForm<AvailabilitySchemaInput, unknown, AvailabilitySchemaOutput>({
		defaultValues: defaultValues,
		resolver: zodResolver(availabilitySchema),
	});

	const [ getAvailability ] = useLazyQuery(GetLocationAvailabilityDocument);

	const [ getLocation ] = useLazyQuery(GetLocationForAvailabilityDocument, {
		onCompleted: (response) => setLocation(response?.location ?? null),
	});

	const [ saveLocationAvailability, { loading: saveLoading } ] = useMutation(SaveLocationAvailabilityDocument);
	const [ saveRepeatedLocationAvailability, { loading: repeatLoading } ] = useMutation(SaveRepeatedLocationAvailabilityDocument);

	const loading = saveLoading || repeatLoading;

	const values = watch();
	const cranes = location?.quays?.flatMap(it => it.cranes ?? []) || [];
	const bolders = location?.quays?.flatMap(it => it.bolders ?? []) || [];

	const isCraneType = values.type === LocationAvailabilityType.CraneAvailability;
	const isQuayType = values.type === LocationAvailabilityType.MooringAvailability;
	const isContainerCrane = values.crane?.craneType === CraneType.Container;
	const craneSpeedEnabled = isQuayType || (isCraneType && !values.crane) || (isContainerCrane);
	const selectedCraneData = cranes.find(c => c.id === values.crane?.id);

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
		setLocation(null);
	};

	const findQuayByBolderId = (id?: number) => {
		return location?.quays?.find((quay) => quay.bolders?.find((bolder) => bolder.id === id));
	};

	const onSave = (data: AvailabilitySchemaOutput) => {

		const { repeatType, repeatDayInterval, endDate, days, ...input } = data;

		if (!isCraneType) {
			const quayFrom = findQuayByBolderId(data.bolderFrom?.id || undefined);
			const quayTo = findQuayByBolderId(data.bolderTo?.id || undefined);

			if (quayFrom?.id !== quayTo?.id) {
				showSnackbar(intl.formatMessage({ id: "QUAY_PLANNING_WARNING_PLAN_MULTI_QUAYS" }), { variant: "error" });
				return;
			}
		}

		if (repeatType === AvailabilityRepeatType.Interval) {
			const differenceInDays = moment(data.toDateTime).diff(moment(data.fromDateTime), "days");

			if (differenceInDays >= (repeatDayInterval ?? 0)) {
				showSnackbar(intl.formatMessage({ id: "REPEAT_INTERVAL_MUST_BE_LARGER_THAN_DATE_DIFFERENCE" }), { variant: "error" });
				return;
			}

			saveRepeatedLocationAvailability({
				variables: {
					input: {
						repeatDayInterval: repeatDayInterval,
						endDateTime: endDate,
						locationAvailabilityInput: input,
					},
				},
			}).then(() => {
				handleOnClose();
				refetch();
			});
		} else if (repeatType === AvailabilityRepeatType.Days) {
			saveRepeatedLocationAvailability({
				variables: {
					input: {
						days: days,
						locationAvailabilityInput: input,
					},
				},
			}).then(() => {
				handleOnClose();
				refetch();
			});
		} else {
			saveLocationAvailability({
				variables: {
					input: input,
				},
			}).then(() => {
				handleOnClose();
				refetch();
			});
		}
	};

	useEffect(() => {
		if (availabilityId) {
			getAvailability({
				variables: {
					id: availabilityId,
				},
			}).then((response) => {
				reset({
					id: response.data?.locationAvailability?.id ?? null,
					location: defaultLocation ?? null,
					type: response.data?.locationAvailability?.type ?? defaultValues.type,
					crane: response.data?.locationAvailability.crane ?? null,
					amountOfCranes: response.data?.locationAvailability.amountOfCranes ?? null,
					craneSpeed: response.data?.locationAvailability.craneSpeed ?? null,
					fromDateTime: response.data?.locationAvailability.fromDateTime ?? null,
					toDateTime: response.data?.locationAvailability.toDateTime ?? null,
					bolderFrom: response.data?.locationAvailability.bolderFrom ?? null,
					bolderTo: response.data?.locationAvailability.bolderTo ?? null,
					maxWidthInMeters: response.data?.locationAvailability.maxWidthInMeters ?? null,
					overflowLocations: response.data?.locationAvailability.overflowLocations ?? null,
					canHandleOOG: response.data?.locationAvailability.canHandleOOG || false,
					canHandleBundles: response.data?.locationAvailability.canHandleBundles || false,
					fullContainersAllowed: response.data?.locationAvailability.fullContainersAllowed || false,
					twinningAllowed: response.data?.locationAvailability.twinningAllowed || false,
				});

				if (defaultLocation) {
					getLocation({
						variables: {
							id: defaultLocation.id,
						},
					});
				}
			});
		} else if (defaultLocation) {
			getLocation({
				variables: {
					id: defaultLocation.id,
				},
			});
		}
	}, [ open, availabilityId ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "QUAY_AVAILABILITY" })}
			form='availabilityForm'
			loading={loading}
			maxWidth="md">
			<React.Fragment>
				<form onSubmit={(event) => void handleSubmit(onSave)(event)} id="availabilityForm">
					<Grid container spacing={3}>
						<Grid size={6}>
							<ClosingHoursWarning values={values} location={location} />

							<FormGroup
								label={intl.formatMessage({ id: "LOCATION" })}
								labelPlacement="left-center"
								overflow="hidden"
								required>
								<Controller
									name='location'
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<LocationSelect
												{...field}
												disabled={!!defaultLocation}
												types={locationTypes ?? undefined}
												onChange={(value) => {
													field.onChange(value);

													if (value) {
														getLocation({
															variables: {
																id: value.id,
															},
														});
													} else {
														setLocation(null);
													}

													setValue("crane", null);
													setValue("amountOfCranes", null);
													setValue("craneSpeed", null);
													setValue("bolderFrom", null);
													setValue("bolderTo", null);
													setValue("maxWidthInMeters", null);
													setValue("overflowLocations", null);
													setValue("canHandleOOG", false);
													setValue("canHandleBundles", false);
													setValue("fullContainersAllowed", false);
													setValue("twinningAllowed", false);

													clearErrors();
												}} />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>

							{!presetAvailabilityType && (
								<FormGroup
									label={intl.formatMessage({ id: "TYPE" })}
									labelPlacement="left-center"
									overflow="hidden">
									<Controller
										name='type'
										control={control}
										render={({ field }) => (
											<Select
												{...field}
												onChange={(value) => {
													field.onChange(value);
													clearErrors();
												}}
												fetchOptions={[ LocationAvailabilityType.CraneAvailability, LocationAvailabilityType.MooringAvailability ]}
												getOptionLabel={option => option === LocationAvailabilityType.CraneAvailability ? intl.formatMessage({ id: "MOORING_AVAILABILITIES_CRANE" }) : intl.formatMessage({ id: "MOORING" })}
												clearable={false}
												disableSearch
											/>
										)}
									/>
								</FormGroup>
							)}

							{isCraneType && (
								<FormGroup
									label={Language("MOORING_AVAILABILITIES_CRANE")}
									labelPlacement="left-center"
									overflow="hidden"
									required>
									<Controller
										name="crane"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<Select
													{...field}
													fetchOptions={cranes}
													onChange={(value: typeof cranes[number] | null) => {
														field.onChange(value);

														setValue("craneSpeed", value?.craneType === CraneType.Container ? (value.averageCraneSpeedInMovesPerHour ?? null) : null);
														setValue("maxWidthInMeters", value?.craneType === CraneType.Container ? (value.maxWidthInMeters ?? null) : null);
														setValue("canHandleOOG", value?.craneType === CraneType.Container ? (value.canHandleOOG ?? false) : false);
														setValue("canHandleBundles", value?.craneType === CraneType.Container ? (value.canHandleBundles ?? false) : false);
														setValue("fullContainersAllowed", value?.craneType === CraneType.Container ? (value.fullContainersAllowed ?? false) : false);
														setValue("twinningAllowed", value?.craneType === CraneType.Container ? (value.twinningAllowed ?? false) : false);

														clearErrors();
													}}
													clearable={false}
													disableSearch={true} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							)}

							{isQuayType && (
								<FormGroup
									label={Language("MOORING_AVAILABILITIES_CRANES_AMOUNT")}
									labelPlacement="left-center"
									overflow="hidden"
									required>
									<Controller
										name="amountOfCranes"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput {...field} decimalScale={0} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							)}

							<FormGroup
								label={Language("MOORING_AVAILABILITIES_CRANE_SPEED")}
								labelPlacement="left-center"
								overflow="hidden"
								required={craneSpeedEnabled}>
								{craneSpeedEnabled ? (
									<Controller
										name="craneSpeed"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput {...field} addonAfter="mov/h" decimalScale={0} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								) : (
									<div>
										<Button onClick={() => openCraneSpeedModal(true)} size="small" variant="outlined">
											{Language("DETAILS")}
										</Button>
									</div>
								)}
							</FormGroup>

							<FormGroup
								label={Language("MOORING_AVAILABILITIES_FROM")}
								labelPlacement="left-center"
								overflow="hidden"
								required>
								<Controller
									name="fromDateTime"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<DatePicker { ...field } showTimeInput />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>

							<FormGroup
								label={Language("MOORING_AVAILABILITIES_TO")}
								labelPlacement="left-center"
								overflow="hidden"
								required>
								<Controller
									name="toDateTime"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<DatePicker { ...field } showTimeInput />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>

							{!isCraneType &&
								<>
									<FormGroup
										label={ Language("MOORING_AVAILABILITIES_BOLDER_FROM") }
										labelPlacement="left-center"
										overflow="hidden"
										required>
										<Controller
											name="bolderFrom"
											control={ control }
											render={({ field, formState }) => (
												<React.Fragment>
													<Select
														{ ...field }
														fetchOptions={bolders}
														getOptionLabel={option => option.description ?? ""} />
													<FormErrorMessage name={field.name} errors={formState.errors} />
												</React.Fragment>
											)} />
									</FormGroup>

									<FormGroup
										label={ Language("MOORING_AVAILABILITIES_BOLDER_TO") }
										labelPlacement="left-center"
										overflow="hidden"
										required>
										<Controller
											name="bolderTo"
											control={ control }
											render={({ field, formState }) => (
												<React.Fragment>
													<Select
														{ ...field }
														fetchOptions={bolders}
														getOptionLabel={option => option.description ?? ""} />
													<FormErrorMessage name={field.name} errors={formState.errors} />
												</React.Fragment>
											)} />
									</FormGroup>
								</>
							}

							{(isQuayType || isContainerCrane) && (
								<React.Fragment>
									<FormGroup
										label={Language("MOORING_AVAILABILITIES_MAX_WIDTH")}
										labelPlacement="left-center"
										overflow="hidden"
										required>
										<Controller
											name="maxWidthInMeters"
											control={control}
											render={({ field, formState }) => (
												<React.Fragment>
													<NumberInput {...field} addonAfter="m" />
													<FormErrorMessage name={field.name} errors={formState.errors} />
												</React.Fragment>
											)} />
									</FormGroup>

									<FormGroup label={Language("LOCATION_AVAILABILITIES_OVERFLOW_LOCATIONS")} labelPlacement="left-center" overflow="hidden">
										<Controller
											name="overflowLocations"
											control={control}
											render={({ field }) => <OverflowLocationSelect {...field} values={values} />} />
									</FormGroup>

									<FormGroup label={Language("MOORING_AVAILABILITIES_CAN_HANDLE_OOG")} labelPlacement="left-center" overflow="hidden">
										<Controller
											name="canHandleOOG"
											control={control}
											render={({ field }) => <Checkbox onChange={field.onChange} checked={field.value} />} />
									</FormGroup>

									<FormGroup label={Language("MOORING_AVAILABILITIES_CAN_HANDLE_BUNDLES")} labelPlacement="left-center" overflow="hidden">
										<Controller
											name="canHandleBundles"
											control={control}
											render={({ field }) => <Checkbox onChange={field.onChange} checked={field.value} />} />
									</FormGroup>

									<FormGroup label={Language("MOORING_AVAILABILITIES_CAN_HANDLE_FULL_CONTAINERS")} labelPlacement="left-center" overflow="visible">
										<Controller
											name="fullContainersAllowed"
											control={control}
											render={({ field }) => <Checkbox onChange={field.onChange} checked={field.value} />} />
									</FormGroup>

									<FormGroup label={Language("MOORING_AVAILABILITIES_TWINNING_ALLOWED")} labelPlacement="left-center" overflow="hidden">
										<Controller
											name="twinningAllowed"
											control={control}
											render={({ field }) => <Checkbox onChange={field.onChange} checked={field.value} />} />
									</FormGroup>
								</React.Fragment>
							)}
						</Grid>

						{!values.id && (
							<React.Fragment>
								<Grid size={1} />

								<Grid size={5}>
									<FormGroup
										label={Language("REPEAT_SETTINGS")}
										labelPlacement="left-center"
										overflow="hidden">
										<Controller
											name="repeatType"
											control={control}
											render={({ field }) => (
												<Select
													{...field}
													fetchOptions={Object.values(AvailabilityRepeatType)}
													getOptionLabel={option => {
														if (option === AvailabilityRepeatType.None) {
															return intl.formatMessage({ id: "NONE" });
														} else if (option === AvailabilityRepeatType.Interval) {
															return intl.formatMessage({ id: "INTERVAL" });
														} else {
															return intl.formatMessage({ id: "STANDARD_CLOSINGS_REPEAT_DAYS" });
														}
													}}
													clearable={false}
													disableSearch
												/>
											)} />
									</FormGroup>

									{values.repeatType === AvailabilityRepeatType.Interval && (
										<React.Fragment>
											<FormGroup
												label={Language("MOORING_AVAILABILITIES_REPEAT_DAYS_SHORT")}
												labelPlacement="left-center"
												overflow="hidden"
												required>
												<Controller
													name="repeatDayInterval"
													control={control}
													render={({ field, formState }) => (
														<React.Fragment>
															<NumberInput {...field} decimalScale={0} addonAfter={intl.formatMessage({ id: "DAYS" })} />
															<FormErrorMessage name={field.name} errors={formState.errors} />
														</React.Fragment>
													)} />
											</FormGroup>

											<FormGroup
												label={Language("MOORING_AVAILABILITIES_END_DATE")}
												labelPlacement="left-center"
												overflow="hidden"
												required>
												<Controller
													name="endDate"
													control={control}
													render={({ field, formState }) => (
														<React.Fragment>
															<DatePicker { ...field } showTimeInput />
															<FormErrorMessage name={field.name} errors={formState.errors} />
														</React.Fragment>
													)} />
											</FormGroup>

											<AmountWarning values={values} />
										</React.Fragment>
									)}

									{values.repeatType === AvailabilityRepeatType.Days && (
										<React.Fragment>
											<FormGroup
												label={Language("DAYS")}
												labelPlacement="left-center"
												overflow="hidden"
												required>
												<Controller
													name="days"
													control={control}
													render={({ field, formState }) => (
														<React.Fragment>
															<DatePicker
																value={undefined}
																highlightDates={field.value?.map(day => new Date(day))}
																shouldCloseOnSelect={false}
																onChange={(timestamp) => {
																	if (timestamp) {
																		const newDays = [ ...(field.value ?? []) ];

																		if (!newDays.includes(timestamp)) {
																			newDays.push(timestamp);
																			field.onChange(newDays);
																		}
																	}
																}} />
															<FormErrorMessage name={field.name} errors={formState.errors} />
														</React.Fragment>
													)} />
											</FormGroup>

											<div>
												{values.days?.sort().map((day, index) => (
													<Chip
														key={index}
														label={<FormattedDate date={day} format="dd, ll" withoutYear />}
														size="small"
														style={{ margin: "4px 4px 0 0" }}
														onDelete={() => {
															const newDays = [ ...(values.days ?? []) ];
															newDays.splice(index, 1);
															setValue("days", newDays.length > 0 ? newDays : null);
														}} />
												))}
											</div>
										</React.Fragment>
									)}
								</Grid>
							</React.Fragment>
						)}
					</Grid>
				</form>

				<CraneSpeedDialog
					open={craneSpeedModalOpen}
					onClose={() => openCraneSpeedModal(false)}
					handlingRates={selectedCraneData?.handlingRates ?? []} />
			</React.Fragment>
		</FormDialog>
	);
};

const ClosingHoursWarning = ({ values, location }: { values: AvailabilitySchemaInput; location: NonNullable<GetLocationForAvailabilityQuery["location"]> | null }) => {

	const enableWarning = () => {

		if (values.location && values.fromDateTime) {
			const openHours = getOpenHoursWindow(location?.openingHours, values.fromDateTime);
			return openHours && (values.fromDateTime < openHours.from || values.fromDateTime > openHours.to);
		} else if (values.location && values.toDateTime) {
			const openHours = getOpenHoursWindow(location?.openingHours, values.toDateTime);
			return openHours && (values.toDateTime < openHours.from || values.toDateTime > openHours.to);
		} else {
			return false;
		}
	};

	if (enableWarning()) {
		return (
			<Alert severity="warning" style={{ marginBottom: 16 }}>
				{intl.formatMessage({ id: "LOCATION_CLOSED" })}
			</Alert>
		);
	} else {
		return <React.Fragment />;
	}
};

const AmountWarning = ({ values }: { values: AvailabilitySchemaInput }) => {

	if (values.fromDateTime && values.repeatDayInterval && values.endDate) {

		const diffInDays = moment(values.endDate).endOf("day").diff(moment(values.fromDateTime), "days");
		const totalObjects = (Math.floor(diffInDays / Number(values.repeatDayInterval)));

		return (
			<Alert severity="warning" style={{ marginBottom: 16 }}>
				{intl.formatMessage({ id: "THIS_WILL_CREATE_X_AVAILABILITIES" }).replace("_X_", totalObjects.toString())}
			</Alert>
		);
	} else {
		return <React.Fragment />;
	}
};

const OverflowLocationSelect = ({ value, onChange, values }: Omit<SelectProps<LocationSelectOption, true>, "fetchOptions"> & { values: AvailabilitySchemaInput }) => {

	const [ options, setOptions ] = useState<LocationSelectOption[]>([]);

	const enabled = values.location && values.fromDateTime && values.toDateTime && (values.toDateTime > values.fromDateTime);

	useEffect(() => {
		onChange?.(null, false);

		if (enabled) {
			request({
				url: `location-availabilities/overflow-candidates/${values.location?.id}?from=${values.fromDateTime}&to=${values.toDateTime}`,
			}).then((response) => setOptions(response as LocationSelectOption[]));
		}
	}, [ values.location?.id, values.fromDateTime, values.toDateTime ]);

	return (
		<Select
			value={value}
			fetchOptions={options}
			onChange={onChange}
			getOptionLabel={option => option.description}
			disabled={!enabled}
			placeholder={!enabled ? intl.formatMessage({ id: "SPECIFY_FROM_TO" }) : ""}
			multiple />
	);
};

export default AvailabilityCreateEditDialog;
